import { useState } from 'react'
import { useAuthStore } from '../hooks/useAuth'
import {
  Shield, Lock, Eye, Fingerprint, Key, Smartphone, AlertTriangle,
  CheckCircle, XCircle, Clock, History, Globe, Monitor
} from 'lucide-react'
import toast from 'react-hot-toast'

interface SecurityLog {
  id: string
  action: string
  ip: string
  location: string
  device: string
  timestamp: string
  status: 'success' | 'failed' | 'warning'
}

export default function SecurityCenter() {
  const { user } = useAuthStore()
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.twoFactorEnabled || false)
  const [show2FASetup, setShow2FASetup] = useState(false)
  const [activeSessions, setActiveSessions] = useState(3)

  const securityLogs: SecurityLog[] = [
    { id: '1', action: 'Login', ip: '192.168.1.1', location: 'Cairo, Egypt', device: 'Chrome / Windows', timestamp: '2026-05-05 16:45', status: 'success' },
    { id: '2', action: 'Password Change', ip: '192.168.1.1', location: 'Cairo, Egypt', device: 'Chrome / Windows', timestamp: '2026-05-05 14:20', status: 'success' },
    { id: '3', action: 'Login Attempt', ip: '45.123.45.67', location: 'Unknown', device: 'Firefox / Linux', timestamp: '2026-05-04 23:15', status: 'failed' },
    { id: '4', action: 'Exam Started', ip: '192.168.1.1', location: 'Cairo, Egypt', device: 'Chrome / Windows', timestamp: '2026-05-04 10:00', status: 'success' },
    { id: '5', action: 'Anti-Cheat Warning', ip: '192.168.1.1', location: 'Cairo, Egypt', device: 'Chrome / Windows', timestamp: '2026-05-04 10:15', status: 'warning' },
  ]

  const toggle2FA = () => {
    if (!twoFactorEnabled) {
      setShow2FASetup(true)
    } else {
      setTwoFactorEnabled(false)
      toast.success('Two-factor authentication disabled')
    }
  }

  const enable2FA = () => {
    setTwoFactorEnabled(true)
    setShow2FASetup(false)
    toast.success('Two-factor authentication enabled!')
  }

  const terminateSession = () => {
    setActiveSessions(prev => Math.max(1, prev - 1))
    toast.success('Session terminated')
  }

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-success" />
      case 'failed': return <XCircle className="w-4 h-4 text-danger" />
      case 'warning': return <AlertTriangle className="w-4 h-4 text-warning" />
      default: return null
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-6 h-6 text-primary" />
          Security Center
        </h1>
        <p className="text-slate-500 mt-1">Manage your account security and monitor activity</p>
      </div>

      {/* Security Score */}
      <div className="glass rounded-xl p-6 border border-primary/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Security Score: 85/100</h3>
              <p className="text-sm text-slate-500">Your account is well protected</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 text-success">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Strong Password</span>
            </div>
            <div className="flex items-center gap-2 text-warning">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">2FA Not Enabled</span>
            </div>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 2FA */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Two-Factor Authentication</h3>
                <p className="text-sm text-slate-500">Add an extra layer of security</p>
              </div>
            </div>
            <button
              onClick={toggle2FA}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                twoFactorEnabled ? 'bg-primary' : 'bg-slate-700'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          {twoFactorEnabled && (
            <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
              <p className="text-sm text-success flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> 2FA is active and protecting your account
              </p>
            </div>
          )}
        </div>

        {/* Active Sessions */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <Monitor className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Active Sessions</h3>
                <p className="text-sm text-slate-500">{activeSessions} devices logged in</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Globe className="w-4 h-4 text-slate-500" />
                <span>Chrome / Windows (Current)</span>
              </div>
              <span className="text-xs text-success">Active</span>
            </div>
            {activeSessions > 1 && (
              <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-slate-500" />
                  <span>Mobile App / iOS</span>
                </div>
                <button
                  onClick={terminateSession}
                  className="text-xs text-danger hover:underline"
                >
                  Terminate
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Password */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <Key className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Password</h3>
                <p className="text-sm text-slate-500">Last changed 14 days ago</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => toast.info('Password change form would open here')}
            className="w-full py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm transition-colors"
          >
            Change Password
          </button>
        </div>

        {/* Anti-Cheat Status */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Anti-Cheat Monitoring</h3>
                <p className="text-sm text-slate-500">Exam security status</p>
              </div>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Screen Recording</span>
              <span className="text-success">Enabled</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Tab Switch Detection</span>
              <span className="text-success">Enabled</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Copy/Paste Blocking</span>
              <span className="text-success">Enabled</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Camera Proctoring</span>
              <span className="text-success">Enabled</span>
            </div>
          </div>
        </div>
      </div>

      {/* Security Logs */}
      <div className="glass rounded-xl p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <History className="w-5 h-5 text-primary" />
          Security Activity Log
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left p-3 text-xs font-medium text-slate-500">Action</th>
                <th className="text-left p-3 text-xs font-medium text-slate-500">IP Address</th>
                <th className="text-left p-3 text-xs font-medium text-slate-500">Location</th>
                <th className="text-left p-3 text-xs font-medium text-slate-500">Device</th>
                <th className="text-left p-3 text-xs font-medium text-slate-500">Time</th>
                <th className="text-left p-3 text-xs font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {securityLogs.map(log => (
                <tr key={log.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="p-3 text-sm">{log.action}</td>
                  <td className="p-3 text-sm text-slate-500 font-mono">{log.ip}</td>
                  <td className="p-3 text-sm text-slate-500">{log.location}</td>
                  <td className="p-3 text-sm text-slate-500">{log.device}</td>
                  <td className="p-3 text-sm text-slate-500">{log.timestamp}</td>
                  <td className="p-3">{getStatusIcon(log.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Setup Two-Factor Authentication</h2>
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                <div className="w-32 h-32 bg-white rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <div className="w-28 h-28 bg-slate-900 rounded flex items-center justify-center">
                    <Key className="w-12 h-12 text-white" />
                  </div>
                </div>
                <p className="text-sm text-slate-500">Scan this QR code with your authenticator app</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Verification Code</label>
                <input
                  type="text"
                  placeholder="Enter 6-digit code"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary text-center text-lg tracking-widest"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShow2FASetup(false)}
                  className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={enable2FA}
                  className="flex-1 py-2.5 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors"
                >
                  Enable 2FA
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
